import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, ShieldCheck } from "lucide-react";
import { LOGO_URL, BRAND, TAGLINE } from "@/lib/brand";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const type = params.get("type");
  const isStaff = type === "staff";
  const isOwnerInvite = type === "owner";
  const isTeam = isStaff || isOwnerInvite;
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const endpoint = isOwnerInvite ? "/auth/accept-owner-invite" : isStaff ? "/auth/accept-staff-invite" : "/auth/accept-invite";
      const { data } = await api.post(endpoint, { token, password: pw });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      setUser(data);
      toast.success(isOwnerInvite ? "Welcome — your agency is ready" : isStaff ? "Welcome to the team" : "Welcome — your portal is ready");
      navigate(isTeam ? "/app" : "/portal");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <span className="bg-white rounded-lg p-1.5"><img src={LOGO_URL} alt={BRAND} className="h-10 w-auto object-contain" /></span>
          <div>
            <div className="font-display font-extrabold">{BRAND}</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C1121F]">{TAGLINE}</div>
          </div>
        </div>
        <div className="rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#C1121F]/40 bg-[#C1121F]/10 px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-[#9B0E18]">
            <ShieldCheck className="h-3.5 w-3.5" /> {isOwnerInvite ? "Agency Owner" : isStaff ? "Team Access" : "Client Portal"}
          </div>
          <h1 className="mt-4 font-display text-2xl font-extrabold flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#C1121F]" /> Set your password</h1>
          {!token ? (
            <p className="mt-4 text-sm text-rose-600">Missing or invalid invite link. Please use the link from your email.</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="set-password-form">
              <div>
                <Label className="text-neutral-600">New password</Label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 bg-[#F6EEEE] border-neutral-200 h-11" data-testid="set-password-input" />
              </div>
              <div>
                <Label className="text-neutral-600">Confirm password</Label>
                <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1.5 bg-[#F6EEEE] border-neutral-200 h-11" data-testid="set-password-confirm-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="set-password-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isOwnerInvite ? "Create My Agency" : isStaff ? "Join the Team" : "Activate Portal")}
              </Button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
